import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { SKIP_AUTH } from '../config/auth';

const AuthContext = createContext();

const TOKEN_KEY = 'courtscribe_token';
const USER_KEY = 'courtscribe_user';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const mapApiUser = (apiUser, fallbackEmail = '') => ({
  id: apiUser.user_id || apiUser.id,
  email: apiUser.email,
  first_name: apiUser.first_name || apiUser.full_name?.split(' ')[0] || fallbackEmail.split('@')[0],
  last_name: apiUser.last_name || apiUser.full_name?.split(' ').slice(1).join(' ') || 'User',
  is_active: apiUser.is_active !== false,
  phone_number: apiUser.phone || apiUser.phone_number,
  role: String(apiUser.role || 'registrar').toLowerCase().replace(/\s+/g, '_'),
  court_name: apiUser.court_name || apiUser.court || '',
  tenant_id: apiUser.tenant_id || null,
  must_change_password: Boolean(apiUser.must_change_password),
});

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      const userData = localStorage.getItem(USER_KEY);

      if (token && userData) {
        try {
          const storedUser = JSON.parse(userData);
          setUser(storedUser);
          setIsAuthenticated(true);
          // Refresh profile from API so role/status match the DB
          try {
            const resp = await authAPI.me();
            const apiUser = resp.data?.data?.user || resp.data?.user;
            if (apiUser) {
              const fresh = mapApiUser(apiUser, storedUser.email);
              setUser(fresh);
              localStorage.setItem(USER_KEY, JSON.stringify(fresh));
            }
          } catch (err) {
            if (err.response?.status === 401) {
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem(USER_KEY);
              setUser(null);
              setIsAuthenticated(false);
            }
          }
        } catch (error) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const persistSessionFromAuth = (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const envelope = response.data?.data || response.data || {};
      const apiUser = envelope.user || envelope;
      const token = envelope.token || response.data?.token;
      if (!token || !apiUser) {
        return { success: false, error: 'Invalid login credentials.' };
      }

      const userData = mapApiUser(apiUser, credentials.email);
      persistSessionFromAuth(userData, token);
      return { success: true, data: { data: userData } };
    } catch (error) {
      const status = error.response?.status;
      const apiError = error.response?.data?.error || error.response?.data?.message;
      const code = error.response?.data?.code;
      if (code === 'account_deactivated' || status === 403) {
        return {
          success: false,
          error:
            apiError ||
            'Your account has been deactivated. Please reach out to your Court Admin for reactivation.',
        };
      }
      if (status === 401) {
        return { success: false, error: apiError || 'Invalid login credentials.' };
      }
      return {
        success: false,
        error: apiError || 'Login failed. Please try again.',
      };
    }
  };

  const register = async (userData) => {
    if (SKIP_AUTH) {
      return {
        success: false,
        error: 'Registration is disabled. Contact your Court Admin for an account.',
      };
    }

    try {
      const response = await authAPI.register(userData);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setIsAuthenticated(false);
    navigate('/login', { replace: true });
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    persistSessionFromAuth,
    mustChangePassword: Boolean(user?.must_change_password),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
